<template>
  <payment-template>
    <base-header
      slot="header"
      :title="$t('in.process')"
      :language="getLanguage"
    ></base-header>

    <div slot="content">
      <payment-view
        :payment="paymentData"
        :recipient="recipientData"
      ></payment-view>

      <div :class="$style.button">
        <link-button
          :href="getProjectUrl"
          variant="secondary"
          disabled
        >
          {{ $t('return.to.site') }}
        </link-button>
      </div>
    </div>

    <base-footer
      slot="footer"
      security
    ></base-footer>
  </payment-template>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex';

import HTTPService from '@/services/Http';

import BaseFooter from 'molecules/base/BaseFooter';
import LinkButton from 'molecules/common/LinkButton';
import BaseHeader from 'organisms/base/BaseHeader';
import PaymentView from 'organisms/payment/PaymentView';
import PaymentTemplate from 'templates/PaymentTemplate';

const COUNT_REQUEST_INTERVAL = 3000;
const COUNT_REQUEST_LIMIT = 20;

export default {
  name: 'PendingPayment',
  components: {
    BaseFooter,
    LinkButton,
    BaseHeader,
    PaymentView,
    PaymentTemplate,
  },
  created() {
    this.loadData();
  },
  data() {
    return {
      loading: false,
      intervalId: null,
      intervalCounterIndex: 0,
      applePaySession: null,
    };
  },
  computed: {
    hasStoppedInterval() {
      return this.intervalCounterIndex >= COUNT_REQUEST_LIMIT;
    },
    paymentId() {
      const { query } = this.$route;

      if (!this.$validate.isObjectWithFields(query, ['paymentId'])) {
        return this.getPaymentId;
      }

      if (this.$validate.isEmptyString(query.paymentId)) {
        return this.getPaymentId;
      }

      return query.paymentId;
    },
    processToken() {
      const { query } = this.$route;

      if (!this.$validate.isObjectWithFields(query, ['processToken'])) {
        return this.getProcessToken;
      }

      if (this.$validate.isEmptyString(query.processToken)) {
        return this.getProcessToken;
      }

      return query.processToken;
    },
    paymentData() {
      return {
        id: this.getDisplayPaymentId,
        amount: this.getDisplayAmount,
        date: this.getFormattedPaymentDate,
        subscription: this.getSubscription,
        processToken: this.processToken,
      };
    },
    recipientData() {
      return {
        project: {
          name: this.getDisplayProjectName,
          url: this.getProjectUrl,
        },
        merchant: {
          email: this.getDisplayMerchantEmail,
        },
        status: 'LOADING',
      };
    },
    ...mapState(['TRANSACTION']),
    ...mapGetters([
      'getLanguage',
      'getProjectUrl',
      'getDisplayPaymentId',
      'getDisplayAmount',
      'getFormattedPaymentDate',
      'getDisplayProjectName',
      'getDisplayMerchantEmail',
      'getSubscription',
      'getApplePaySession',
      'isDigitalContent',
      'getPaymentId',
      'getProcessToken',
      'isPaymentAutoRedirection',
      'getSuccessUrl',
      'getFailUrl',
    ]),
  },
  methods: {
    async loadData() {
      this.loading = true;

      try {
        await this.GET_PAYMENT({
          paymentId: this.paymentId,
          processToken: this.processToken,
        });
      } catch (error) {
        this.processingErrorResponse(error);
      }

      this.initializeApplePaySession();
      this.paymentStatusRequestByInterval();

      this.loading = false;
    },
    initializeApplePaySession() {
      this.applePaySession = this.getApplePaySession;
    },
    paymentStatusRequestByInterval() {
      this.intervalId = setInterval(() => {
        if (this.hasStoppedInterval) {
          clearInterval(this.intervalId);
          this.$router.push({ name: 'expired.payment' });
          return;
        }

        this.intervalCounterIndex++;
        this.getPaymentStatus();
      }, COUNT_REQUEST_INTERVAL);
    },
    // TODO: @danealton нужно использовать GET_PAYMENT из экшенов, но надо проверить что возвращаются данные о платеже при успехе и ошибке
    getPaymentStatus() {
      HTTPService.getPaymentStatus(this.paymentId, this.processToken)
        .then(response => {
          const { data } = response;

          this.SET_TRANSACTION(data);
          this.SET_TRANSACTION_KEY(data.billCode || data.projectCode);
          this.processingPaymentResponse(data);
        })
        .catch(error => {
          this.processingErrorResponse(error);
        });
    },
    // TODO: эта механика дублируется в EnterCard.vue надо подумать как избавиться
    processingPaymentResponse(data) {
      const { status } = data;
      console.log('PROCESSING_PAYMENT::STATUS', status);

      if (this.$validate.isUndefined(status)) {
        return;
      }
      if (status === 'PROCESSING') {
        return;
      }

      clearInterval(this.intervalId);
      this.completeApplePaymentByStatus(status);
      this.redirectAfterPaymentByStatus(status);
    },
    // TODO: эта механика дублируется в EnterCard.vue надо подумать как избавиться
    processingErrorResponse(error) {
      if (!error.data) {
        this.$router.push({ name: 'service.error' });
        return;
      }

      const { status } = error.data;
      console.log('PAYMENT_ERROR::STATUS', status);

      if (this.$validate.isUndefined(status)) {
        return;
      }

      clearInterval(this.intervalId);
      this.redirectAfterPaymentByStatus(status);
    },
    completeApplePaymentByStatus(status) {
      if (this.$validate.isNull(this.applePaySession)) {
        return;
      }

      const applePaymentStatus = status === 'SUCCESS'
        ? ApplePaySession.STATUS_SUCCESS // eslint-disable-line no-undef
        : ApplePaySession.STATUS_FAILURE; // eslint-disable-line no-undef

      console.log('APPLY_PAYMENT::STATUS', applePaymentStatus);

      this.applePaySession.completePayment(applePaymentStatus);
    },
    redirectAfterPaymentByStatus(status) {
      let pageName = 'expired.payment';

      if (status === 'SUCCESS') {
        if (this.isPaymentAutoRedirection) {
          window.location.assign(this.getSuccessUrl);
          return;
        }

        pageName = this.isDigitalContent ? 'PaidDigitalSelling' : 'paid.payment';
      }

      if (status === 'DECLINE' || status === 'FRAUD_DECLINE') {
        if (this.isPaymentAutoRedirection) {
          window.location.assign(this.getFailUrl);
          return;
        }

        pageName = 'rejected.payment';
      }

      if (status === 'FAIL') {
        if (this.isPaymentAutoRedirection) {
          window.location.assign(this.getFailUrl);
          return;
        }

        pageName = 'failed.payment';
      }

      this.$router.push({ name: pageName });
    },
    ...mapActions(['GET_PAYMENT']),
    ...mapMutations(['SET_TRANSACTION', 'SET_TRANSACTION_KEY']),
  },
};
</script>

<style lang="sass" module>
  .button
    margin-top: 20px

    +respond-to('medium')
      margin-top: 15px
</style>
